import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { lazy, Suspense } from "react";
import { Provider } from "react-redux";
import { store } from "./store";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import { PermissionsProvider } from "./contexts/permissions-context";
import { AuthExpirationHandler } from "./components/AuthExpirationHandler";
import "./App.css";
import { Box, CircularProgress } from "@mui/material";
// ✅ LAZY LOADING: Las páginas se cargan solo cuando el usuario navega a ellas
// Esto reduce el bundle inicial de ~2MB a ~500KB
// Páginas públicas (se cargan inmediatamente si es necesario)
const CallbackPage = lazy(() => import("./pages/CallbackPage"));
const Login = lazy(() => import("./pages/Login"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
// Dashboard
const Dashboard = lazy(() => import("./pages/Dashboard"));
// Maestros
const MaestrosPage = lazy(() => import("./pages/Maestros/MaestrosPage"));
const CountriesListViewRefactored = lazy(
  () => import("./components/masters/countries/CountriesListViewRefactored"),
);
const CarriersListViewRefactored = lazy(
  () => import("./components/masters/carriers/CarriersListViewRefactored"),
);
const CurrenciesListViewRefactored = lazy(
  () => import("./components/masters/currencies/CurrenciesListViewRefactored"),
);
const EquipmentListViewRefactored = lazy(
  () => import("./components/masters/equipment/EquipmentListViewRefactored"),
);
const NomenclatorsListViewRefactored = lazy(
  () =>
    import("./components/masters/nomenclators/NomenclatorsListViewRefactored"),
);
const PortsListViewRefactored = lazy(
  () => import("./components/masters/ports/PortsListViewRefactored"),
);
const InvoiceConceptsListViewRefactored = lazy(
  () =>
    import("./components/masters/invoice-concepts/InvoiceConceptsListViewRefactored"),
);
const ContactsListViewRefactored = lazy(
  () => import("./components/masters/contacts/ContactsListViewRefactored"),
);
const UsersListViewRefactored = lazy(
  () => import("./components/masters/users/UsersListViewRefactored"),
);
const CompaniesListViewRefactored = lazy(
  () => import("./components/masters/companies/CompaniesListViewRefactored"),
);
const CustomsAgentsListView = lazy(
  () => import("./components/masters/customs-agents/CustomsAgentsListView"),
);
const TariffConceptsListViewRefactored = lazy(
  () =>
    import("./components/masters/tariff-concepts/TariffConceptsListViewRefactored"),
);
const EntityAliasesListViewRefactored = lazy(
  () =>
    import("./components/masters/entity-aliases/EntityAliasesListViewRefactored"),
);
const MappingsListViewRefactored = lazy(
  () => import("./components/masters/mappings/MappingsListViewRefactored"),
);
// Página genérica para maestros basados en configuración
const GenericMasterPage = lazy(() => import("./pages/GenericMasterPage"));

// Configuración
const ProfilePage = lazy(() => import("./pages/Configuracion/ProfilePage"));
// Ayuda
const AyudaPage = lazy(() => import("./pages/Ayuda/AyudaPage"));
const SoportePage = lazy(() => import("./pages/Ayuda/SoportePage"));
// Contratos y Tarifas
const ContractsListViewRefactored = lazy(
  () => import("./components/contracts/ContractsListViewRefactored"),
);
const TariffsListViewRefactored = lazy(
  () => import("./components/tariffs/TariffsListViewRefactored"),
);
// Bookings
const BookingsListViewRefactored = lazy(
  () => import("./components/bookings/bookings-list-view-refactored"),
);
// Finanzas - Páginas principales
const InternalInvoicesPage = lazy(
  () => import("./pages/Finanzas/InternalInvoicesPage"),
);
// Finanzas - Líneas (vistas principales con row spanning)
const SupplierInvoiceLinesListView = lazy(
  () =>
    import("./components/finance/supplier-invoices/SupplierInvoiceLinesListView"),
);
const InternalInvoiceLinesListView = lazy(
  () =>
    import("./components/finance/internal-invoices/InternalInvoiceLinesListView"),
);
// Operaciones
const OperacionesPage = lazy(
  () => import("./pages/Operaciones/OperacionesPage"),
);
// Empresas (Resumen por empresa)
const EmpresasResumenPage = lazy(
  () => import("./pages/Empresas/EmpresasResumenPage"),
);
const EmpresasTransportSummaryPage = lazy(
  () => import("./pages/Empresas/EmpresasTransportSummaryPage"),
);
const EmpresasIncotermSummaryPage = lazy(
  () => import("./pages/Empresas/EmpresasIncotermSummaryPage"),
);
// Importaciones
const ImportacionesPage = lazy(
  () => import("./pages/Importaciones/ImportacionesPage"),
);
const ImportarCargarArchivoPage = lazy(
  () => import("./pages/ImportarCargarArchivoPage"),
);
const ImportTypeVerificationPage = lazy(
  () => import("./pages/Importaciones/ImportTypeVerificationPage"),
);
// Testing (solo desarrollo)
const Test2View = lazy(() => import("./pages/Test2View"));
// ✅ Componente de loading para Suspense
const PageLoader = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "50vh",
    }}
  >
    <CircularProgress size={40} />
  </Box>
);
function App() {
  const auth = useAuth();
  // Mostrar loading mientras se verifica la autenticación
  if (auth.isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress />
          <Box component="p" sx={{ mt: 2, color: "text.secondary" }}>
            Cargando...
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Provider store={store}>
      <PermissionsProvider>
        {/* ✅ Manejador de expiración de token - escucha eventos 401 y redirige al login */}
        <AuthExpirationHandler />
        <Layout>
          {/* ✅ Suspense envuelve todas las rutas para mostrar loader durante lazy loading */}
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Redirect de "/" a "/dashboard" */}
              <Route index element={<Navigate to="/dashboard" replace />} />
              {/* Rutas públicas */}
              <Route path="auth/callback" element={<CallbackPage />} />
              <Route path="access-denied" element={<AccessDenied />} />
              {/* Rutas privadas */}
              <Route
                path="dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="maestros"
                element={
                  <ProtectedRoute>
                    <MaestrosPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/paises"
                element={
                  <ProtectedRoute>
                    <CountriesListViewRefactored />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/navieras"
                element={
                  <ProtectedRoute>
                    <CarriersListViewRefactored />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/monedas"
                element={
                  <ProtectedRoute>
                    <CurrenciesListViewRefactored />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/equipos"
                element={
                  <ProtectedRoute>
                    <EquipmentListViewRefactored />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/nomencladores"
                element={
                  <ProtectedRoute>
                    <NomenclatorsListViewRefactored />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/puertos"
                element={
                  <ProtectedRoute>
                    <PortsListViewRefactored />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/empresas"
                element={
                  <ProtectedRoute>
                    <CompaniesListViewRefactored />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/agentes-aduanales"
                element={
                  <ProtectedRoute>
                    <CustomsAgentsListView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/conceptos"
                element={
                  <ProtectedRoute>
                    <InvoiceConceptsListViewRefactored />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/contactos"
                element={
                  <ProtectedRoute>
                    <ContactsListViewRefactored />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/conceptos-tarifa"
                element={
                  <ProtectedRoute>
                    <TariffConceptsListViewRefactored />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/mapeo-maestros"
                element={
                  <ProtectedRoute>
                    <EntityAliasesListViewRefactored />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/mapeos"
                element={
                  <ProtectedRoute>
                    <MappingsListViewRefactored />
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Códigos HS */}
              <Route
                path="/maestros/hs-codes"
                element={
                  <ProtectedRoute>
                    <GenericMasterPage viewKey="hs-codes-list" />
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Incoterms */}
              <Route
                path="/maestros/incoterms"
                element={
                  <ProtectedRoute>
                    <GenericMasterPage viewKey="incoterms-list" />
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Región Comercial */}
              <Route
                path="/maestros/regiones-comerciales"
                element={
                  <ProtectedRoute>
                    <GenericMasterPage viewKey="commercial-regions-list" />
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Ejecutivo de Ventas */}
              <Route
                path="/maestros/ejecutivos-ventas"
                element={
                  <ProtectedRoute>
                    <GenericMasterPage viewKey="sales-executives-list" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/proveedores"
                element={
                  <ProtectedRoute>
                    <GenericMasterPage viewKey="suppliers-list" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maestros/compradores"
                element={
                  <ProtectedRoute>
                    <GenericMasterPage viewKey="buyers-list" />
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Configuración */}
              <Route
                path="/configuracion/usuarios"
                element={
                  <ProtectedRoute>
                    <UsersListViewRefactored />
                  </ProtectedRoute>
                }
              />
              {/* <Route
                path="/configuracion/empresas"
                element={
                  <ProtectedRoute>
                    <CompaniesListViewRefactored />
                  </ProtectedRoute>
                }
              /> */}
              {/* Ruta de Agentes Aduanales */}
              <Route
                path="/configuracion/agentes-aduanales"
                element={
                  <ProtectedRoute>
                    <GenericMasterPage viewKey="customs-agents-list" />
                  </ProtectedRoute>
                }
              />
              {/* Ruta de Perfil */}
              <Route
                path="/perfil"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Ayuda */}
              <Route
                path="/ayuda"
                element={
                  <ProtectedRoute>
                    <AyudaPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ayuda/soporte"
                element={
                  <ProtectedRoute>
                    <SoportePage />
                  </ProtectedRoute>
                }
              />
              {/* Ruta de Login */}
              <Route path="/login" element={<Login />} />
              {/* Ruta de prueba */}
              <Route
                path="/test2"
                element={
                  <ProtectedRoute>
                    <Test2View />
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Contratos */}
              <Route
                path="/contratos"
                element={
                  <ProtectedRoute>
                    <ContractsListViewRefactored />
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Tarifas */}
              <Route
                path="/tarifas"
                element={
                  <ProtectedRoute>
                    <TariffsListViewRefactored />
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Bookings */}
              <Route
                path="/bookings"
                element={
                  <ProtectedRoute>
                    <BookingsListViewRefactored />
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Operaciones */}
              <Route
                path="/operaciones"
                element={
                  <ProtectedRoute>
                    <OperacionesPage />
                  </ProtectedRoute>
                }
              />
              {/* Ruta de Empresas (Resumen por empresa) */}
              <Route
                path="/empresas"
                element={
                  <ProtectedRoute>
                    <EmpresasResumenPage />
                  </ProtectedRoute>
                }
              />
              {/* Ruta de Resumen por Método de Transporte */}
              <Route
                path="/empresas/resumen-transporte"
                element={
                  <ProtectedRoute>
                    <EmpresasTransportSummaryPage />
                  </ProtectedRoute>
                }
              />
              {/* Ruta de Resumen por Incoterm */}
              <Route
                path="/empresas/resumen-incoterm"
                element={
                  <ProtectedRoute>
                    <EmpresasIncotermSummaryPage />
                  </ProtectedRoute>
                }
              />
              {/* Finanzas - Facturas de Proveedores */}
              <Route
                path="/finanzas/facturas"
                element={
                  <ProtectedRoute>
                    <SupplierInvoiceLinesListView />
                  </ProtectedRoute>
                }
              />
              {/* Finanzas - Facturas Internas (líneas con info de booking) */}
              <Route
                path="/finanzas/facturas-internas"
                element={
                  <ProtectedRoute>
                    <InternalInvoicesPage />
                  </ProtectedRoute>
                }
              />
              {/* Finanzas - Líneas de Facturas Internas (reales) */}
              <Route
                path="/finanzas/facturas-internas/lineas"
                element={
                  <ProtectedRoute>
                    <InternalInvoiceLinesListView />
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Importaciones */}
              <Route
                path="/importaciones"
                element={
                  <ProtectedRoute>
                    <ImportacionesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/importar/cargar-archivo"
                element={
                  <ProtectedRoute>
                    <ImportarCargarArchivoPage />
                  </ProtectedRoute>
                }
              />
              {/* Verificación de importType */}
              <Route
                path="/importar/verificar-tipo"
                element={
                  <ProtectedRoute>
                    <ImportTypeVerificationPage />
                  </ProtectedRoute>
                }
              />
              {/* Ruta 404 */}
              <Route path="*" element={<div>Página no encontrada</div>} />
            </Routes>
          </Suspense>
        </Layout>
      </PermissionsProvider>
    </Provider>
  );
}
export default App;
